<template>
  <div>
    <h1 class="text-2xl font-bold mb-4">Editorial Section</h1>
    <p class="mb-6">Manage Editorial section here.</p>

    <!-- Loading and Errors -->
    <div v-if="loading" class="text-center text-gray-500">Loading posts...</div>
    <div v-else-if="error" class="text-center text-red-500">{{ error }}</div>
    <div v-else-if="posts.length === 0" class="text-center text-gray-500">No posts found.</div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
        <thead>
          <tr class="bg-gray-100">
            <th class="px-4 py-2 text-center text-sm font-medium text-gray-700">ID</th>
            <th class="px-4 py-2 text-center text-sm font-medium text-gray-700">Title</th>
            <th class="px-4 py-2 text-center text-sm font-medium text-gray-700">Subtitle</th>
            <th class="px-4 py-2 text-center text-sm font-medium text-gray-700">Description</th>
            <th class="px-4 py-2 text-center text-sm font-medium text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="post in posts"
            :key="post.id"
            class="border-t border-gray-200 hover:bg-gray-50"
          >
            <td class="px-4 py-2 text-sm text-gray-600">{{ post.id }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ post.title || 'N/A' }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ truncateContent(post.subtitle) }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ truncateContent(post.description) }}</td>
            <td class="px-4 py-2 text-center">
              <button
                @click="openEditModal(post)"
                class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
              >
                Edit
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Edit Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50"
    >
      <div class="bg-white rounded-lg shadow-lg w-[700px] p-6">
        <h2 class="text-xl font-bold mb-4 text-gray-800">Edit Editorial</h2>

        <div class="space-y-3">
          <input
            v-model="editForm.title"
            type="text"
            placeholder="Title"
            class="w-full border border-gray-300 rounded px-3 py-2"
          />
          <input
            v-model="editForm.subtitle"
            type="text"
            placeholder="Subtitle"
            class="w-full border border-gray-300 rounded px-3 py-2"
          />
          <textarea
            v-model="editForm.description"
            placeholder="Description"
            rows="5"
            class="w-full border border-gray-300 rounded px-3 py-2 resize-none"
          ></textarea>
        </div>

        <div class="flex justify-end space-x-3 mt-5">
          <button
            @click="showModal = false"
            class="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            @click="updateEditorial"
            class="px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'

// State variables
const posts = ref([])
const loading = ref(false)
const error = ref(null)
const showModal = ref(false)
const editForm = ref({ id: null, title: '', subtitle: '', description: '' })

// Fetch all editorials
const fetchPosts = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/editorials`)
    if (!response.ok) throw new Error('Failed to fetch posts')
    const data = await response.json()
    posts.value = data
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

// Open edit modal and fill form
const openEditModal = (post) => {
  editForm.value = { ...post }
  showModal.value = true
}

// Update editorial
const updateEditorial = async () => {
  const { id, title, subtitle, description } = editForm.value
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/editorials/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, subtitle, description }),
    })

    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Failed to update')

    showModal.value = false
    await fetchPosts() // refresh table
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.message,
      confirmButtonColor: '#BF3131'
    });
  }
}

// Truncate function
const truncateContent = (content) => {
  if (!content) return 'N/A'
  return content.length > 100 ? content.substring(0, 100) + '...' : content
}

// Initial load
onMounted(fetchPosts)
</script>
